import {
  Autocomplete,
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useRef, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "react-beautiful-dnd";
import {
  applyInferredSectionType,
  createEmptySection,
  sectionEditorPromptValue,
  sectionEditorTextValue,
  type TemplateSection,
} from "./templateSerializer";
import { TEMPLATE_MAIN_TOPICS, TEMPLATE_TOPIC_BY_KEY } from "./templateTopics";

const FIELD_LABEL_SX = {
  display: "block",
  fontSize: "11.5px",
  fontWeight: 600,
  color: "#374151",
  mb: "6px",
};

const FIELD_HELPER_SX = {
  display: "block",
  fontSize: "11.5px",
  color: "#6B7280",
  mt: "6px",
  lineHeight: 1.45,
};

const FIELD_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#FAFAFA",
    fontSize: "13.5px",
    borderRadius: "8px",
    "& fieldset": { borderColor: "#E5E7EB", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#D1D5DB" },
    "&.Mui-focused": { bgcolor: "#fff" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
};

const HEADER_TITLE_INPUT_SX = {
  flex: 1,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    bgcolor: "transparent",
    fontSize: "13px",
    fontWeight: 700,
    borderRadius: "6px",
    "& fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: "#E5E7EB" },
    "&.Mui-focused": { bgcolor: "#fff" },
    "&.Mui-focused fieldset": { borderColor: "primary.main" },
  },
  "& .MuiOutlinedInput-input": {
    py: "6px",
    px: "8px",
  },
};

const FIELD_TEXTAREA_SX = {
  ...FIELD_INPUT_SX,
  "& .MuiOutlinedInput-root": {
    ...FIELD_INPUT_SX["& .MuiOutlinedInput-root"],
    fontSize: "13px",
    alignItems: "flex-start",
    py: 1,
  },
};

const HEADING_SELECT_SX = {
  width: "100%",
  height: 36,
  fontSize: "13px",
  fontWeight: 600,
  bgcolor: "#FAFAFA",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
    borderWidth: "1.5px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#D1D5DB",
  },
};

type HeadingSelectValue = "normal" | "1" | "2" | "3" | "4" | "5" | "6";

function headingSelectValue(section: TemplateSection): HeadingSelectValue {
  if (section.useSectionHeading === false) return "normal";
  return String(section.headingLevel) as HeadingSelectValue;
}

function SectionInsertDivider({
  onInsert,
  disabled,
}: {
  onInsert: () => void;
  disabled?: boolean;
}) {
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label="Add section here"
      onClick={disabled ? undefined : onInsert}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onInsert();
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        py: "6px",
        cursor: disabled ? "default" : "pointer",
        opacity: 0.35,
        transition: "opacity 0.15s ease",
        "&:hover": disabled ? {} : { opacity: 0.85 },
      }}
    >
      <Box sx={{ flex: 1, height: "1px", bgcolor: "#D1D5DB" }} />
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          border: "1.5px dashed",
          borderColor: "primary.main",
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.paper",
          flexShrink: 0,
        }}
      >
        <AddIcon sx={{ fontSize: 13 }} />
      </Box>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "#D1D5DB" }} />
    </Box>
  );
}

export interface TemplateSectionEditorProps {
  sections: TemplateSection[];
  onChange: (sections: TemplateSection[]) => void;
  readOnly?: boolean;
  sectionsCollapseKey?: string;
}

export default function TemplateSectionEditor({
  sections,
  onChange,
  readOnly = false,
  sectionsCollapseKey = "default",
}: TemplateSectionEditorProps) {
  /** Sections in this set are expanded; default is all collapsed. */
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  useEffect(() => {
    setExpandedIds(new Set());
  }, [sectionsCollapseKey]);

  const isCollapsed = (id: string) => !expandedIds.has(id);

  const toggleCollapsed = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateSection = (id: string, patch: Partial<TemplateSection>) => {
    const touchesContent = Object.keys(patch).some((k) => k !== "activeTab");
    onChange(
      sections.map((s) => {
        if (s.id !== id) return s;
        let next = applyInferredSectionType({ ...s, ...patch });
        if (touchesContent) next.rawBlock = undefined;
        return next;
      }),
    );
  };

  const removeSection = (id: string) => {
    if (sections.length <= 1) {
      onChange([createEmptySection()]);
      return;
    }
    onChange(sections.filter((s) => s.id !== id));
  };

  const insertSectionAt = (index: number) => {
    const newSection = createEmptySection();
    const next = [...sections];
    next.splice(index, 0, newSection);
    onChange(next);
    setExpandedIds((prev) => new Set(prev).add(newSection.id));
  };

  const handleDragEnd = (result: DropResult) => {
    if (readOnly || !result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    const next = [...sections];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next.map((s) => ({ ...s, rawBlock: undefined })));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="template-sections" isDropDisabled={readOnly}>
        {(droppableProvided) => (
          <Stack
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
            sx={{ pb: 2 }}
          >
            {sections.map((section, index) => {
              const effective = applyInferredSectionType(section);
              const textValue = sectionEditorTextValue(section);
              const promptValue = sectionEditorPromptValue(section);
              const isAi = effective.sectionType === "ai";
              const collapsed = isCollapsed(section.id);
              const displayName =
                section.title.trim() || `Section ${index + 1}`;

              return (
                <Draggable
                  key={section.id}
                  draggableId={section.id}
                  index={index}
                  isDragDisabled={readOnly}
                >
                  {(draggableProvided, snapshot) => (
                    <Box
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      sx={{ mb: snapshot.isDragging ? 1 : 0 }}
                    >
                      <SectionInsertDivider
                        onInsert={() => insertSectionAt(index)}
                        disabled={readOnly}
                      />

                      <Box
                        sx={{
                          bgcolor: "#fff",
                          border: "1px solid",
                          borderColor: snapshot.isDragging
                            ? "primary.main"
                            : "#E5E7EB",
                          borderRadius: "12px",
                          overflow: "hidden",
                          boxShadow: snapshot.isDragging
                            ? "0 4px 12px rgba(0,0,0,0.08)"
                            : "none",
                        }}
                      >
                        {/* Header — old collapsible bar: name + controls; body holds text + instructions */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.75,
                            pr: 1.25,
                            bgcolor: collapsed ? "#fff" : "#FAFBFC",
                            borderBottom: collapsed
                              ? "none"
                              : "1px solid #F3F4F6",
                          }}
                        >
                          <IconButton
                            size="small"
                            disabled={readOnly}
                            aria-label="Drag to reorder section"
                            sx={{
                              p: 0.5,
                              flexShrink: 0,
                              cursor: readOnly ? "default" : "grab",
                              color: "#D1D5DB",
                              "&:active": { cursor: "grabbing" },
                            }}
                            {...draggableProvided.dragHandleProps}
                          >
                            <DragIndicatorIcon sx={{ fontSize: 18 }} />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => toggleCollapsed(section.id)}
                            aria-label={
                              collapsed ? "Expand section" : "Collapse section"
                            }
                            aria-expanded={!collapsed}
                            sx={{
                              p: 0.5,
                              width: 24,
                              height: 24,
                              flexShrink: 0,
                              color: "#6B7280",
                              transform: collapsed
                                ? "rotate(-90deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                            }}
                          >
                            <ExpandMoreIcon sx={{ fontSize: 18 }} />
                          </IconButton>

                          <TextField
                            value={section.title}
                            onChange={(e) =>
                              updateSection(section.id, {
                                title: e.target.value,
                              })
                            }
                            placeholder={displayName}
                            size="small"
                            disabled={readOnly}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            aria-label="Section title"
                            sx={HEADER_TITLE_INPUT_SX}
                          />

                          <FormControl
                            size="small"
                            disabled={readOnly}
                            sx={{ flexShrink: 0, width: 108 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Select
                              value={headingSelectValue(section)}
                              onChange={(e) => {
                                const v = e.target.value as HeadingSelectValue;
                                if (v === "normal") {
                                  updateSection(section.id, {
                                    useSectionHeading: false,
                                    headingLevel: 2,
                                  });
                                } else {
                                  updateSection(section.id, {
                                    useSectionHeading: true,
                                    headingLevel: Number(
                                      v,
                                    ) as TemplateSection["headingLevel"],
                                  });
                                }
                              }}
                              sx={HEADING_SELECT_SX}
                              aria-label="Heading level"
                            >
                              <MenuItem value="1">H1</MenuItem>
                              <MenuItem value="2">H2</MenuItem>
                              <MenuItem value="3">H3</MenuItem>
                              <MenuItem value="4">H4</MenuItem>
                              <MenuItem value="5">H5</MenuItem>
                              <MenuItem value="6">H6</MenuItem>
                              <MenuItem value="normal">Normal text</MenuItem>
                            </Select>
                          </FormControl>

                          <IconButton
                            size="small"
                            onClick={() => removeSection(section.id)}
                            disabled={readOnly}
                            aria-label="Remove section"
                            sx={{
                              width: 28,
                              height: 28,
                              flexShrink: 0,
                              color: "#6B7280",
                              "&:hover": {
                                bgcolor: "#FEE2E2",
                                color: "#DC2626",
                              },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>

                        <Collapse in={!collapsed}>
                          <Stack spacing={2} sx={{ px: 2, py: 2 }}>
                          <Box>
                            <Tooltip
                              title="Fixed text that will be inserted exactly as typed, right before the AI's output. Useful for standard structural headers, disclaimers, or standard formatting (e.g., 'Location:', 'Attendees:')."
                              placement="top-start"
                              arrow
                            >
                              <Typography component="label" sx={FIELD_LABEL_SX}>
                                Text prefix
                              </Typography>
                            </Tooltip>
                            <TextField
                              multiline
                              minRows={3}
                              maxRows={12}
                              value={textValue}
                              onChange={(e) =>
                                updateSection(section.id, {
                                  textPrefix: e.target.value,
                                })
                              }
                              placeholder="e.g. Location:\n\nAttendees:"
                              fullWidth
                              disabled={readOnly}
                              size="small"
                              sx={FIELD_TEXTAREA_SX}
                            />
                          </Box>

                          <Box>
                            <Tooltip
                              title="The prompt used to guide the LLM. Tell the AI exactly what to extract, summarize, or analyze from the meeting transcript for this specific section."
                              placement="top-start"
                              arrow
                            >
                              <Typography component="label" sx={FIELD_LABEL_SX}>
                                AI instructions
                              </Typography>
                            </Tooltip>
                            <TextField
                              multiline
                              minRows={4}
                              maxRows={20}
                              value={promptValue}
                              onChange={(e) =>
                                updateSection(section.id, {
                                  prompt: e.target.value,
                                })
                              }
                              placeholder="Describe what the AI should write for this section…"
                              fullWidth
                              disabled={readOnly}
                              size="small"
                              sx={FIELD_TEXTAREA_SX}
                            />
                            {/* <Typography component="span" sx={FIELD_HELPER_SX}>
                              Guides the LLM on what to extract, summarize, or
                              analyze from the meeting for this section. Leave empty
                              for a static-only block.
                            </Typography> */}
                          </Box>

                          {isAi && (
                            <Box>
                              <Typography component="label" sx={FIELD_LABEL_SX}>
                                Assigned topics
                              </Typography>
                              <Autocomplete
                                multiple
                                options={TEMPLATE_MAIN_TOPICS.map((t) => t.key)}
                                value={section.assignedTopics}
                                onChange={(_, value) =>
                                  updateSection(section.id, {
                                    assignedTopics: value,
                                  })
                                }
                                getOptionLabel={(key) =>
                                  TEMPLATE_TOPIC_BY_KEY[key]?.label ?? key
                                }
                                disabled={readOnly}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Add topic…"
                                    sx={{
                                      ...FIELD_INPUT_SX,
                                      "& .MuiOutlinedInput-root": {
                                        ...FIELD_INPUT_SX[
                                          "& .MuiOutlinedInput-root"
                                        ],
                                        minHeight: 36,
                                        py: "3px",
                                      },
                                    }}
                                  />
                                )}
                                renderTags={(value, getTagProps) =>
                                  value.map((key, i) => (
                                    <Chip
                                      {...getTagProps({ index: i })}
                                      key={key}
                                      label={
                                        TEMPLATE_TOPIC_BY_KEY[key]?.label ?? key
                                      }
                                      size="small"
                                      sx={{
                                        height: 22,
                                        bgcolor: "#E3F2FD",
                                        color: "primary.main",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                      }}
                                    />
                                  ))
                                }
                              />
                            </Box>
                          )}
                          </Stack>
                        </Collapse>

                      {index === sections.length - 1 && (
                        <SectionInsertDivider
                          onInsert={() => insertSectionAt(sections.length)}
                          disabled={readOnly}
                        />
                      )}
                      </Box>
                    </Box>
                  )}
                </Draggable>
              );
            })}
            {droppableProvided.placeholder}
          </Stack>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export { createEmptySection };
